import httpRequest from "~/composables/requset";
import type {ApiResponse, ResponseData} from '~/utils/types';
import {DateFormat} from "~/utils/dateFormat";

// AV P2 刀1:場地檔期三態。後端 /api/space/info 與 /api/space/usage-status 皆回傳
// availability 欄位;可立即預約 / 可能可用 / 可詢問(未同步不得顯示為可用,一律降級)。
export type SpaceAvailability = 'reservable' | 'maybe' | 'inquiry'


// 廠商地點列表
export function locationList(data): Promise<ResponseData> {
    return httpRequest(
        'POST',
        '/api/platform/space/location-list',
        data
    )
}

// 空間列表
export function getSpaceList(pagination, selectSpaceType: number, adminID: number, failFunc?: any): Promise<ResponseData> {

    const queryData = {
        pagination,
        type: selectSpaceType,
        admin_id: adminID
    }

    return httpRequest(
        'POST',
        '/api/platform/space/list',
        queryData,
        failFunc
    )
}

// 下拉空間列表 改拿 categories
export function getSpaceTypeList(): Promise<ResponseData> {
    return httpRequest(
        'POST',
        '/api/platform/space/type-list'
    );
}

// 空間詳情
export function getSpaceInfo(id: number, errHandle?: any): Promise<ResponseData> {

    const queryData = {space_id: id}

    return httpRequest(
        'POST',
        '/api/space/info',
        queryData,
        errHandle
    )
}

// 版位點擊計量(置頂卡片等);fire-and-forget,失敗不影響導頁(見 docs/supply-monetization/track-a-spec.md §5)
export function recordSpaceClick(spaceId: number, surface = 'search'): Promise<ResponseData> {
    return httpRequest(
        'POST',
        '/api/space/stat/click',
        {space_id: spaceId, surface}
    )
}

// 聯絡管道點擊計量(電話/LINE 等外部導頁);fire-and-forget,失敗不影響 tel:/mailto: 導頁
export function recordOutboundContactClick(spaceId: number, adminId: number, channel: string): Promise<ResponseData> {
    return httpRequest(
        'POST',
        '/api/space/stat/contact-click',
        {space_id: spaceId, admin_id: adminId, channel}
    )
}

// 搜尋結果曝光計量:一頁結果批次上報一次當頁 space_id;fire-and-forget,失敗不影響瀏覽
export function recordSpaceImpressions(spaceIds: number[]): Promise<ResponseData> {
    return httpRequest(
        'POST',
        '/api/space/stat/impression',
        {space_ids: spaceIds}
    )
}

// 空間使用情形
export function getSpaceUsageStatus(spaceId: number, date: string): Promise<ResponseData> {

    const queryData = {
        space_id: spaceId,
        date: date
    }
    return httpRequest(
        'POST',
        '/api/space/usage-status',
        queryData
    )
}

// 模糊搜尋
export function getSpaceKeywordSearch(data): Promise<ResponseData> {
    return httpRequest(
        'POST',
        '/api/platform/space/location-list/keyword',
        data
    )
}

export function getSearchSpaceList(data): Promise<ResponseData> {
    return httpRequest(
        'POST',
        '/api/platform/spaces',
        data
    )
}

// AV P2 刀2:搜尋卡片批次三態。列表走 Redis 快取(不含 GCal 狀態),故 availability
// 由這支即時 API 疊上去。date 選填(YYYY/MM/DD);帶入則算當日,否則回日期無關可預約性。
export function getSpacesAvailability(spaceIds: number[], date = ''): Promise<ResponseData> {
    return httpRequest(
        'POST',
        '/api/platform/spaces/availability',
        {space_ids: spaceIds, date}
    )
}

// admin id，找該空間列表
export function spaceListByAdmin(data): Promise<ResponseData> {
    return httpRequest(
        'POST',
        '/api/platform/spaces-by-admin',
        data
    )
}

// 首頁給 space_id 拿空間資訊
export function getMainPageSpaceList(data): Promise<ResponseData> {
    return httpRequest(
        'POST',
        '/api/platform/main-page',
        data
    )
}

// 首頁拿熱門等 space_id 給 /api/platform/main-page 用
export function getMainPageSpaceId(): Promise<ResponseData> {
    return httpRequest(
        'POST',
        '/api/platform/main-page-spaces'
    )
}

// AV P6:詳情頁日曆的逐日時段(場主公告的販售時段 − 已知檔期 − 已經過去的時間)。
// 範圍上限 31 天,所以一次只查一個月;回應形狀見 utils/spaceSchedule.ts 的 SpaceSchedule。
//
// 為什麼不用既有的 /api/space/info 的 space_usage_status:那個只給「被占用的區間」,
// 要在 JS 裡再實作一次「販售時段減占用」——而那份運算後端已經有了(micrositeslots),
// 且詢問那道閘用的就是它。前端自己算一套,畫面上顯示的與閘擋的遲早會漂移。
export function getSpaceSchedule(spaceId: number, startDate: string, endDate: string): Promise<ResponseData> {

    const queryData = {
        space_id: spaceId,
        start_date: startDate,
        end_date: endDate
    }

    return httpRequest(
        'POST',
        '/api/space/schedule',
        queryData
    )
}

// AV P6:這個場地這一天這個時段行不行(conflict 五態 ＋ 當天還訂得到的區間)。
// 端點本身不擋任何東西,只回答問題——擋在後端 inquiry.CreateInquiry,那裡是撞期零寫入。
//
// time 選填、自由文字(`14:00~18:00`、`14:00-18:00`、「下午」都收),後端解析不出來就回
// none(不擋)。date 缺或看不懂回 unknown,也不是錯誤。
export function checkSpaceSlot(spaceId: number, date: string, time: string): Promise<ResponseData> {

    const queryData = {
        space_id: spaceId,
        date: date,
        time: time
    }

    return httpRequest(
        'POST',
        '/api/space/slot-check',
        queryData
    )
}
